import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Pencil, Trash2, Upload, Loader2, X, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { compressImage } from "@/lib/compress-image";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/landmarks")({
  component: AdminLandmarksPage,
});

type Landmark = {
  id: string;
  slug: string;
  name: string;
  architect: string | null;
  year: string | null;
  area: string | null;
  blurb: string | null;
  gradient: string | null;
  image_url: string | null;
  sort_order: number;
  active: boolean;
};

const DEFAULT_GRADIENT = "linear-gradient(135deg, oklch(0.55 0.10 200), oklch(0.32 0.08 250))";

function slugify(s: string) {
  return s.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80) || "landmark";
}

export function AdminLandmarksPage() {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "landmarks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("landmarks")
        .select("*")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Landmark[];
    },
  });

  const [editing, setEditing] = useState<Landmark | null>(null);
  const [creating, setCreating] = useState(false);

  async function toggleActive(row: Landmark) {
    const { error } = await supabase.from("landmarks").update({ active: !row.active }).eq("id", row.id);
    if (error) toast.error(error.message);
    else { toast.success(row.active ? "Hidden" : "Published"); refetch(); }
  }

  async function remove(row: Landmark) {
    if (!confirm(`Delete "${row.name}"?`)) return;
    const { error } = await supabase.from("landmarks").delete().eq("id", row.id);
    if (error) toast.error(error.message);
    else { toast.success("Deleted"); refetch(); }
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-[#495057] hover:text-[#212529]">
              <ArrowLeft className="size-4" /> Admin
            </Link>
            <span className="text-[#adb5bd]">›</span>
            <h1 className="text-xl font-bold text-[#212529]">Landmarks</h1>
          </div>
          <button
            onClick={() => { setEditing(null); setCreating(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-[#0d6efd] text-white text-sm font-medium hover:bg-[#0b5ed7]"
          >
            <Plus className="size-4" /> Add landmark
          </button>
        </div>

        <p className="text-sm text-[#6c757d] mb-4">
          Manage the "Landmarks Worth A Detour" cards shown on the Things to do page. Upload images,
          edit copy and re-order — changes are live immediately.
        </p>

        <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 flex justify-center"><Loader2 className="size-5 animate-spin text-[#6c757d]" /></div>
          ) : !data || data.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#6c757d]">No landmarks yet. Add your first one.</div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">

              <thead className="bg-[#f1f3f5] text-[11px] uppercase tracking-wider text-[#495057]">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Image</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Architect</th>
                  <th className="px-3 py-2 text-left">Year</th>
                  <th className="px-3 py-2 text-left">Area</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={row.id} className="border-t hover:bg-[#f8f9fa]">
                    <td className="px-3 py-2 text-[#6c757d]">{row.sort_order}</td>
                    <td className="px-3 py-2">
                      <div
                        className="size-12 rounded overflow-hidden bg-cover bg-center border"
                        style={{
                          backgroundImage: row.image_url ? `url(${row.image_url})` : (row.gradient ?? DEFAULT_GRADIENT),
                        }}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium text-[#212529]">{row.name}</td>
                    <td className="px-3 py-2 text-[#495057]">{row.architect ?? "—"}</td>
                    <td className="px-3 py-2 text-[#495057]">{row.year ?? "—"}</td>
                    <td className="px-3 py-2 text-[#495057]">{row.area ?? "—"}</td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => toggleActive(row)}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                          row.active ? "bg-[#d1e7dd] text-[#0f5132]" : "bg-[#e9ecef] text-[#6c757d]"
                        }`}
                      >
                        {row.active ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
                        {row.active ? "Live" : "Hidden"}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => { setEditing(row); setCreating(false); }}
                          className="p-1.5 rounded hover:bg-[#e9ecef] text-[#495057]"
                          title="Edit"
                        >
                          <Pencil className="size-4" />
                        </button>
                        <button
                          onClick={() => remove(row)}
                          className="p-1.5 rounded hover:bg-[#f8d7da] text-[#842029]"
                          title="Delete"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

      </div>

      {(editing || creating) && (
        <LandmarkEditor
          landmark={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            qc.invalidateQueries({ queryKey: ["admin", "landmarks"] });
            qc.invalidateQueries({ queryKey: ["public-landmarks"] });
          }}
          nextSortOrder={(data?.length ?? 0)}
        />
      )}
    </div>
  );
}

function LandmarkEditor({
  landmark,
  onClose,
  onSaved,
  nextSortOrder,
}: {
  landmark: Landmark | null;
  onClose: () => void;
  onSaved: () => void;
  nextSortOrder: number;
}) {
  const [form, setForm] = useState<Partial<Landmark>>(() =>
    landmark ?? {
      slug: "",
      name: "",
      architect: "",
      year: "",
      area: "",
      blurb: "",
      gradient: DEFAULT_GRADIENT,
      image_url: "",
      sort_order: nextSortOrder,
      active: true,
    },
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  function up<K extends keyof Landmark>(k: K, v: Landmark[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function handleUpload(file: File) {
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const ext = (compressed.name.split(".").pop() || "jpg").toLowerCase();
      const path = `landmarks/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage.from("content-images").upload(path, compressed, {
        cacheControl: "31536000",
        upsert: false,
        contentType: compressed.type || "image/jpeg",
      });
      if (error) throw error;
      const { data } = supabase.storage.from("content-images").getPublicUrl(path);
      up("image_url", data.publicUrl);
      toast.success("Image uploaded");
    } catch (err: any) {
      toast.error(err?.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    const slug = (form.slug?.trim() || slugify(form.name)).toLowerCase();
    setSaving(true);
    try {
      const payload = {
        slug,
        name: form.name!.trim(),
        architect: form.architect?.trim() || null,
        year: form.year?.trim() || null,
        area: form.area?.trim() || null,
        blurb: form.blurb?.trim() || null,
        gradient: form.gradient?.trim() || DEFAULT_GRADIENT,
        image_url: form.image_url?.trim() || null,
        sort_order: Number(form.sort_order ?? 0),
        active: form.active ?? true,
      };
      if (landmark) {
        const { error } = await supabase.from("landmarks").update(payload).eq("id", landmark.id);
        if (error) throw error;
        toast.success("Saved");
      } else {
        const { error } = await supabase.from("landmarks").insert(payload);
        if (error) throw error;
        toast.success("Created");
      }
      onSaved();
    } catch (err: any) {
      toast.error(err?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-lg shadow-xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between z-10">
          <h2 className="font-semibold text-[#212529]">
            {landmark ? "Edit landmark" : "New landmark"}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#e9ecef]"><X className="size-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Image */}
          <div>
            <label className="text-xs font-semibold text-[#495057]">Image</label>
            <div className="mt-1.5 flex items-start gap-3">
              <div
                className="size-28 rounded-lg overflow-hidden bg-cover bg-center border flex-shrink-0"
                style={{
                  backgroundImage: form.image_url ? `url(${form.image_url})` : (form.gradient ?? DEFAULT_GRADIENT),
                }}
              />
              <div className="flex-1 min-w-0 space-y-2">
                <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-[#e9ecef] text-[#212529] text-sm font-medium cursor-pointer hover:bg-[#dee2e6]">
                  {uploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                  {uploading ? "Uploading…" : (form.image_url ? "Replace image" : "Upload image")}
                  <input
                    type="file" accept="image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.currentTarget.value = ""; }}
                  />
                </label>
                {form.image_url && (
                  <button onClick={() => up("image_url", "")} className="text-xs text-[#dc3545] hover:underline block">
                    Remove image (use gradient)
                  </button>
                )}
                <p className="text-[11px] text-[#6c757d]">Tall portraits work best. Max 2MB; we auto-compress.</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name *">
              <input
                className="w-full px-2.5 py-1.5 border rounded text-sm"
                value={form.name ?? ""}
                onChange={(e) => up("name", e.target.value)}
                placeholder="Oodi Central Library"
              />
            </Field>
            <Field label="Slug">
              <input
                className="w-full px-2.5 py-1.5 border rounded text-sm"
                value={form.slug ?? ""}
                onChange={(e) => up("slug", e.target.value)}
                placeholder="auto-from-name"
              />
            </Field>
            <Field label="Architect">
              <input
                className="w-full px-2.5 py-1.5 border rounded text-sm"
                value={form.architect ?? ""}
                onChange={(e) => up("architect", e.target.value)}
                placeholder="ALA Architects"
              />
            </Field>
            <Field label="Year">
              <input
                className="w-full px-2.5 py-1.5 border rounded text-sm"
                value={form.year ?? ""}
                onChange={(e) => up("year", e.target.value)}
                placeholder="2018"
              />
            </Field>
            <Field label="Area / neighborhood">
              <input
                className="w-full px-2.5 py-1.5 border rounded text-sm"
                value={form.area ?? ""}
                onChange={(e) => up("area", e.target.value)}
                placeholder="Töölönlahti"
              />
            </Field>
            <Field label="Sort order">
              <input
                type="number"
                className="w-full px-2.5 py-1.5 border rounded text-sm"
                value={form.sort_order ?? 0}
                onChange={(e) => up("sort_order", Number(e.target.value))}
              />
            </Field>
          </div>

          <Field label="Blurb">
            <textarea
              className="w-full px-2.5 py-1.5 border rounded text-sm min-h-[80px]"
              value={form.blurb ?? ""}
              onChange={(e) => up("blurb", e.target.value)}
              placeholder="A timber-clad civic wave facing parliament — Finland's love letter to public space."
            />
          </Field>

          <Field label="Gradient (CSS, used when no image)">
            <input
              className="w-full px-2.5 py-1.5 border rounded text-sm font-mono text-xs"
              value={form.gradient ?? ""}
              onChange={(e) => up("gradient", e.target.value)}
              placeholder={DEFAULT_GRADIENT}
            />
          </Field>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active ?? true}
              onChange={(e) => up("active", e.target.checked)}
            />
            <span>Published (visible on site)</span>
          </label>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-4 py-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm text-[#495057] hover:bg-[#f1f3f5]">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#0d6efd] text-white text-sm font-medium hover:bg-[#0b5ed7] disabled:opacity-60"
          >
            {saving && <Loader2 className="size-4 animate-spin" />}
            {landmark ? "Save changes" : "Create landmark"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-semibold text-[#495057] block mb-1">{label}</label>
      {children}
    </div>
  );
}
